import { Children, type ReactNode } from "react";

import { classes } from "./classes.js";
import { TONE_CLASSES, type Tone } from "./tone.js";

/**
 * `P0-UIK-001`: a transient confirmation.
 *
 * **`role="status"` and not `role="alert"`.** A toast announces something that
 * already succeeded; an alert interrupts a screen reader mid-sentence. The
 * thing a user MUST read , a refusal, a validation error , belongs in `Field`
 * or in a `Modal`, where it stays on the screen. 4.6.6's rule against a
 * spinner that never ends has a sibling here: a message that vanishes is a
 * message somebody missed.
 */
export interface ToastProps {
  readonly children: ReactNode;
  readonly tone?: Tone;
  readonly onClose?: () => void;
  readonly closeLabel?: string;
  readonly className?: string;
}

export function Toast({
  children,
  tone = "ok",
  onClose,
  closeLabel,
  className,
}: ToastProps): React.JSX.Element {
  return (
    <div
      role="status"
      data-kit="toast"
      className={classes(
        "flex items-center gap-3 rounded-xl px-4 py-3 text-sm",
        TONE_CLASSES[tone],
        className,
      )}
    >
      <span className="flex-1">{children}</span>
      {onClose === undefined ? null : (
        <button type="button" onClick={onClose} aria-label={closeLabel} className="text-xs font-medium">
          ×
        </button>
      )}
    </div>
  );
}

/**
 * `F-0b`: **the bank's stack of toasts: at most two at once**, the newest last, in a live region at the foot of the
 * window. A third would hide a message behind a message; the caller dismisses or lets one expire. Each toast keeps
 * its own `role="status"`; the container adds no second announcement (`aria-live="off"`).
 */
export interface ToastStackProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export function ToastStack({ children, className }: ToastStackProps): React.JSX.Element {
  return (
    <div
      aria-live="off"
      data-kit="toast-stack"
      className={classes(
        "fixed bottom-4 right-4 z-40 flex w-[min(420px,calc(100vw-32px))] flex-col gap-2",
        className,
      )}
    >
      {Children.toArray(children).slice(-2)}
    </div>
  );
}
